import React, {useState} from "react";
import {useQuery} from "react-query";
import moment from "moment";
import {useOidcAccessToken} from "@axa-fr/react-oidc";
import {useAuth} from "../../../../context/user";
import ModalV2 from "../../../../components/portal/ModalV2";
import {triggerLogout} from "../../../../api/authLogout";

type Session = {
    sessionID: string;
    deviceName: string;
    ipAddress: string;
    issuedAt: string;
    expiresAt: string;
    revokedAt: string | null;
};

const authBase = process.env.REACT_APP_OAUTH2_AUTHORITY;

async function fetchSessions(userID: string): Promise<Session[]> {
    const res = await fetch(`${authBase}/oauth/sessions?user_id=${encodeURIComponent(userID)}`);
    if (!res.ok) throw new Error("Failed to load sessions");
    return res.json();
}

async function revokeSession(sessionID: string, userID: string): Promise<void> {
    const body = new URLSearchParams();
    body.set("session_id", sessionID);
    body.set("user_id", userID);
    const res = await fetch(`${authBase}/oauth/revoke-session`, {method: "POST", body});
    if (!res.ok) throw new Error("Failed to revoke session");
}

function isActive(session: Session): boolean {
    return !session.revokedAt && new Date(session.expiresAt) > new Date();
}

type Props = {
    userID: string;
};

const UserSessions: React.FC<Props> = ({userID}) => {
    const {accessTokenPayload} = useOidcAccessToken();
    const currentSessionID: string | undefined = (accessTokenPayload as any)?.sid;
    const {isSuperAdmin} = useAuth();

    const {data: sessions, isLoading, refetch} = useQuery(
        ["user-sessions", userID],
        async () => {
            const data = await fetchSessions(userID);
            return data.sort((a, b) => {
                if (a.sessionID === currentSessionID) return -1;
                if (b.sessionID === currentSessionID) return 1;
                return 0;
            });
        },
        {
            refetchOnWindowFocus: true,
            refetchInterval: isSuperAdmin ? 5000 : false,
        },
    );

    const [confirmSession, setConfirmSession] = useState<Session | null>(null);
    const [revoking, setRevoking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        if (!confirmSession) return;
        setRevoking(true);
        setError(null);
        try {
            await revokeSession(confirmSession.sessionID, userID);
            setConfirmSession(null);
            if (confirmSession.sessionID === currentSessionID) {
                triggerLogout();
                return;
            }
            refetch();
        } catch {
            setError("Failed to revoke session");
        } finally {
            setRevoking(false);
        }
    };

    return (
        <>
            <ModalV2 open={!!confirmSession} setOpen={(open: boolean) => { if (!open) setConfirmSession(null); }} contentClassName="max-w-sm">
                {confirmSession && (
                    <div className="flex flex-col gap-4">
                        <p className="font-semibold text-lg">Revoke session?</p>
                        <div className="flex flex-col gap-1">
                            <p className="text-sm font-medium">{confirmSession.deviceName || "Unknown device"}</p>
                            <p className="text-xs text-light-grey">{confirmSession.ipAddress || "Unknown IP"}</p>
                            <p className="text-xs text-light-grey">Signed in {moment(confirmSession.issuedAt).fromNow()}</p>
                        </div>
                        {confirmSession.sessionID === currentSessionID && (
                            <p className="text-sm text-dark-red font-medium">
                                This is your current session — you will be logged out.
                            </p>
                        )}
                        {error && <p className="text-sm text-red">{error}</p>}
                        <div className="flex flex-row gap-2 justify-end">
                            <button
                                onClick={() => setConfirmSession(null)}
                                className="text-sm text-grey border border-steel rounded px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={revoking}
                                className="text-sm text-white bg-red rounded px-4 py-2 disabled:opacity-50"
                            >
                                {revoking ? "Revoking..." : "Revoke"}
                            </button>
                        </div>
                    </div>
                )}
            </ModalV2>

            <div className="flex flex-col gap-4">
                <p className="text-md text-left font-semibold">Sessions</p>

                {isLoading && <p className="text-sm text-light-grey">Loading sessions...</p>}

                {!isLoading && (!sessions || sessions.length === 0) && (
                    <p className="text-sm text-light-grey">No sessions found.</p>
                )}

                {sessions && sessions.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                        {sessions.map((session) => {
                            const active = isActive(session);
                            return (
                                <div
                                    key={session.sessionID}
                                    className={`border border-steel rounded p-3 flex flex-row justify-between items-center gap-4 ${!active ? "opacity-50" : ""}`}
                                >
                                    <div className="flex flex-col gap-0.5 min-w-0">
                                        <span className="font-medium text-sm truncate">
                                            {session.deviceName || "Unknown device"}
                                            {session.sessionID === currentSessionID && (
                                                <span className="ml-2 text-xs text-blue font-normal">current</span>
                                            )}
                                        </span>
                                        <span className="text-xs text-light-grey">
                                            {session.ipAddress || "Unknown IP"}
                                        </span>
                                        <span className="text-xs text-light-grey">
                                            Signed in {moment(session.issuedAt).fromNow()}
                                            {!active && session.revokedAt && ` · Revoked ${moment(session.revokedAt).fromNow()}`}
                                        </span>
                                    </div>

                                    {active && (
                                        <button
                                            onClick={() => setConfirmSession(session)}
                                            className="text-sm text-dark-red border border-dark-red rounded px-3 py-1 shrink-0 hover:bg-desaturated-red transition-colors"
                                        >
                                            Revoke
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default UserSessions;
